/**
 * ===================================================================================
 * 📁 경로: services/frontend/src/components/ActionPlanLogModal.jsx
 * 📌 프로그램명: 실천 계획 로그 생성/수정 모달 컴포넌트
 * 🧩 개요:
 * - 사용자가 AI가 제안한 실천 계획(Action Plan)에 대한 수행 기록(Log)을 입력하거나
 * 수정할 수 있는 팝업 모달창 UI를 제공합니다.
 * - 이 컴포넌트에서 생성/수정된 데이터는 AI 피드백 시스템의 강화학습(RLHF)을 위한
 * '보상(Reward)' 데이터로 서버에 저장됩니다.
 * ===================================================================================
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';

/**
 * [Why]
 * 사용자가 '건강 일기'를 작성하는 핵심 인터페이스입니다. 신규 로그 생성과 기존 로그 수정을
 * 하나의 컴포넌트에서 처리하여 코드의 재사용성을 높이고, 일관된 사용자 경험을 제공합니다.
 *
 * [How]
 * - **모드 전환**: `logForEdit` prop의 존재 여부로 '수정 모드'와 '생성 모드'를 구분합니다.
 * - **상태 초기화**: `useEffect` 훅을 사용하여 모달이 열릴 때마다(`open` prop 변경)
 * 수정 모드일 경우 전달받은 로그 데이터로, 생성 모드일 경우 현재 날짜/시간으로
 * 폼의 초기 상태를 설정합니다.
 * - **데이터 제출**: '저장' 버튼 클릭 시 `submit` 함수가 실행됩니다. `useRef`로 각 입력 필드의
 * 최신 값을 읽어와 API 요청 본문을 구성하고, 수정/생성 모드에 따라 적절한
 * API 엔드포인트(PATCH 또는 POST)를 호출합니다.
 * - **외부 통신**: `axios`를 사용하여 백엔드 API와 통신합니다. 작업이 성공적으로 완료되면
 * `onSubmitted` 콜백 함수를 호출하여 부모 컴포넌트(페이지)가 데이터 목록을 새로고침하도록
 * 알립니다.
 *
 * @component
 * @param {object} props - 이 컴포넌트가 받는 데이터 객체입니다.
 * @property {boolean} props.open - 모달의 열림/닫힘 상태.
 * @property {Function} props.onClose - 모달을 닫을 때 호출되는 콜백 함수.
 * @property {Function} props.onSubmitted - 로그 저장(생성/수정) 성공 시 호출되는 콜백 함수.
 * @property {object} [props.plan] - (생성 모드) 로그를 추가할 대상 실천 계획 객체.
 * @property {number} props.reportId - 현재 작업중인 리포트의 ID.
 * @property {object} [props.logForEdit] - (수정 모드) 수정할 대상 로그 객체.
 * @property {number} [props.planIdForCreate] - (생성 모드) 로그를 추가할 대상 실천 계획 ID.
 * @returns {JSX.Element | null} `open` prop이 true일 때만 모달 JSX 엘리먼트를 반환합니다.
 */
export default function ActionPlanLogModal({ open, onClose, plan, reportId, onSubmitted, logForEdit, planIdForCreate }) {
  const [form, setForm] = useState({
    log_date: '',            // 'YYYY-MM-DD'
    performed_time: '',      // 최종 조합된 'HH:mm' (24시간제) - 제출 시 생성
    completion_status: 'completed',
    source: 'manual',
    quantity: '',
    unit: '',
    user_rating: '',
    user_memo: '',
  });
  const [error, setError] = useState('');
  const [units, setUnits] = useState([]);
  const [unitsError, setUnitsError] = useState('');
  // [추가]
  // [변경 사유]
  // - "기록 조회" 화면(ActionPlanLogsPage)에서는 모달 호출 시 plan 객체를 넘기지 않고(plan=null)
  //   planIdForCreate만 넘기는 케이스가 있습니다.
  // - 이 경우 기존 UI는 상단 실천항목 표시가 "실천 항목"으로만 노출되어 사용자 혼란이 발생했습니다.
  // - 모달 내부에서 reportId + planId로 플랜 정보를 보강 조회한 뒤, 표시 영역에도 반영합니다.
  const [displayPlan, setDisplayPlan] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  // 초기값 세팅 완료 여부(기본값이 입력창에 반영되도록 초기화 완료 전 렌더를 지연)
  const [ready, setReady] = useState(false);

  // 12시간제 시간 입력을 위한 UI 전용 상태
  const [meridiem, setMeridiem] = useState('AM'); // 'AM' | 'PM'
  const [hour12, setHour12] = useState('');       // '1'..'12'
  const [minute, setMinute] = useState('');       // '00'..'59'

  // 제어되지 않는(uncontrolled) 입력을 위해 ref 사용
  const dateRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const qtyRef = useRef(null);
  const unitRef = useRef(null);
  const ratingRef = useRef(null);
  const memoRef = useRef(null);
  const statusRef = useRef(null);

  // 유틸: label 병기
  const unitLabel = (u) => {
    if (!u) return '';
    const val = String(u).toLowerCase();
    // 기존 매핑 유지
    if (val === 'min' || val === 'mins' || val === 'minute' || val === 'minutes') return '분(min)';
    if (val === 'hour' || val === 'hours' || val === 'hr' || val === 'hrs') return '시간(hours)';
    if (val === 'km') return '킬로미터(km)';
    if (val === 'kcal') return '킬로칼로리(kcal)';
    // 요청 매핑 추가
    if (val === 'count') return '회(count)';
    if (val === 'kg') return '킬로그램(kg)';
    if (val === 'percent') return '비율(percent)';
    if (val === 'bpm') return '심박수(bpm)';
    if (val === 'mg_dl') return '혈당(mg/dL)';
    if (val === 'mm_hg') return '혈압(mmHg)';
    return `${u}`; // 원본 그대로
  };

  // [Why]
  // - 기존에는 생성 모드에서 단위를 무조건 'min'으로 초기화하여, 식습관/금주/체중 등 성격이 다른 플랜도
  //   모두 "분(min)"처럼 보이는 UX 문제가 있었습니다.
  //
  // [How]
  // - ActionPlan 데이터(target_unit/target_quantity/measurable_metric/category)가 있으면 이를 우선 반영합니다.
  // - 값이 비어 있으면 suggestion 문장(예: "15분", "주 2회", "최소 4일")에서 숫자/단위를 추출해 추천합니다.
  const _deriveDefaultsFromPlan = (planObj) => {
    const suggestion = String(planObj?.suggestion || '').trim();
    const category = String(planObj?.category || '').trim();
    const metric = String(planObj?.measurable_metric || '').trim();
    const targetUnit = (planObj?.target_unit !== undefined && planObj?.target_unit !== null) ? String(planObj.target_unit).trim() : '';
    const targetQtyRaw = planObj?.target_quantity;
    const targetQty = (targetQtyRaw !== undefined && targetQtyRaw !== null && !Number.isNaN(Number(targetQtyRaw))) ? Number(targetQtyRaw) : null;

    const metricToUnit = {
      steps: 'count',
      distance: 'km',
      heart_rate: 'bpm',
      hrv: 'bpm',
      blood_pressure_systolic: 'mm_hg',
      blood_pressure_diastolic: 'mm_hg',
      blood_glucose: 'mg_dl',
      body_weight: 'kg',
      body_fat_percentage: 'percent',
      active_energy: 'kcal',
      sleep: 'hours',
    };

    const tokenToUnit = {
      min: 'min',
      mins: 'min',
      minute: 'min',
      minutes: 'min',
      '분': 'min',
      hour: 'hours',
      hours: 'hours',
      hr: 'hours',
      hrs: 'hours',
      '시간': 'hours',
      km: 'km',
      '킬로미터': 'km',
      kg: 'kg',
      '킬로그램': 'kg',
      count: 'count',
      '회': 'count',
      '일': 'count',
      percent: 'percent',
      '%': 'percent',
      bpm: 'bpm',
    };

    const pickFromSuggestion = () => {
      if (!suggestion) return null;
      // 예: "15분", "15 분", "주 2회", "최소 4일", "2회 이하" 등
      const m = suggestion.match(/(\d+(?:\.\d+)?)\s*(분|시간|회|일|km|kg|%|bpm)/);
      if (m) {
        const qty = Number(m[1]);
        const unitCode = tokenToUnit[m[2]];
        if (!Number.isNaN(qty) && unitCode) return { quantity: qty, unit: unitCode };
      }
      // "주 2회"처럼 사이에 문자가 끼는 케이스 보강
      const m2 = suggestion.match(/주\s*(\d+)\s*회/);
      if (m2) {
        const qty = Number(m2[1]);
        if (!Number.isNaN(qty)) return { quantity: qty, unit: 'count' };
      }
      // "일주일에 최소 4일" 형태
      const m3 = suggestion.match(/(\d+)\s*일/);
      if (m3) {
        const qty = Number(m3[1]);
        if (!Number.isNaN(qty)) return { quantity: qty, unit: 'count' };
      }
      return null;
    };

    // 1) 단위 우선순위: target_unit > measurable_metric 매핑 > suggestion 추출 > category 기본값
    let unit = '';
    if (targetUnit) unit = targetUnit;
    else if (metric && metricToUnit[metric]) unit = metricToUnit[metric];
    else {
      const picked = pickFromSuggestion();
      if (picked?.unit) unit = picked.unit;
    }

    if (!unit) {
      if (category === 'physical_activity') unit = 'min';
      else if (category === 'sleep_management') unit = 'hours';
      else if (category === 'dietary_habit') unit = 'count';
      else unit = 'count';
    }

    // 2) 수량 우선순위: target_quantity(양수만) > suggestion 추출 > category 기본값(보수적)
    let quantity = '';
    if (targetQty !== null && targetQty > 0) quantity = String(targetQty);
    else {
      const picked = pickFromSuggestion();
      if (picked?.quantity !== undefined && picked?.quantity !== null && !Number.isNaN(Number(picked.quantity))) {
        quantity = String(picked.quantity);
        unit = picked.unit || unit;
      }
    }

    if (!quantity) {
      if (category === 'physical_activity') quantity = '30';
      else if (category === 'dietary_habit') quantity = '1';
      else if (category === 'sleep_management') quantity = '7';
    }

    return { unit, quantity };
  };

  // 모달이 열리거나 편집 대상 로그가 변경될 때마다 폼 상태를 초기화합니다.
  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      // 새로 열릴 때 초기화 플래그 리셋
      setReady(false);
      // [추가] 표시용 플랜은 우선 props(plan)를 사용하고, 이후 필요 시 보강 조회 결과로 덮어씁니다.
      setDisplayPlan(plan || null);
      const now = new Date();
      const yyyy = now.getFullYear();
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      const isoDate = `${yyyy}-${mm}-${dd}`;

      // 편집 모드이면 기존 로그로 초기화, 아니면 현재 시각으로 초기화
      if (logForEdit) {
        try {
          // 백엔드가 date(YYYY-MM-DD)로 반환할 수 있으므로 문자열이면 그대로 사용
          const dateStr = (typeof logForEdit.log_date === 'string' && /\d{4}-\d{2}-\d{2}/.test(logForEdit.log_date))
            ? logForEdit.log_date
            : (() => {
                const d = logForEdit.log_date ? new Date(logForEdit.log_date) : now;
                return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              })();
          // performed_at이 있으면 사용
          const p = logForEdit.performed_at ? new Date(logForEdit.performed_at) : null;
          if (p) {
            const pH = p.getHours();
            const pM = p.getMinutes();
            const md = pH >= 12 ? 'PM' : 'AM';
            let h12v = pH % 12; if (h12v === 0) h12v = 12;
            setMeridiem(md);
            setHour12(String(h12v).padStart(2, '0'));
            setMinute(String(pM).padStart(2, '0'));
          } else {
            // 없으면 기본값
            const h24 = now.getHours();
            const m = now.getMinutes();
            const md = h24 >= 12 ? 'PM' : 'AM';
            let h12 = h24 % 12; if (h12 === 0) h12 = 12;
            setMeridiem(md);
            setHour12(String(h12).padStart(2, '0'));
            setMinute(String(m).padStart(2, '0'));
          }
          setForm({
            log_date: dateStr,
            performed_time: '',
            completion_status: logForEdit.completion_status || 'completed',
            source: 'manual',
            quantity: (logForEdit.quantity ?? '') + '',
            unit: logForEdit.unit || 'min',
            user_rating: (logForEdit.user_rating ?? '') + '',
            user_memo: logForEdit.user_memo || '',
          });
        } catch (e) {
          // 실패 시 기본 초기화로 폴백
          const h24 = now.getHours();
          const m = now.getMinutes();
          const md = h24 >= 12 ? 'PM' : 'AM';
          let h12 = h24 % 12; if (h12 === 0) h12 = 12;
          setMeridiem(md);
          setHour12(String(h12).padStart(2, '0'));
          setMinute(String(m).padStart(2, '0'));
          setForm({
            log_date: isoDate,
            performed_time: '',
            completion_status: 'completed',
            source: 'manual',
            quantity: '',
            unit: 'min',
            user_rating: '',
            user_memo: '',
          });
        }
      } else {
        // 생성 모드 기본값: 현재 날짜/시각 + (플랜 기반 수량/단위 추천)
        const h24 = now.getHours();
        const m = now.getMinutes();
        const md = h24 >= 12 ? 'PM' : 'AM';
        let h12 = h24 % 12;
        if (h12 === 0) h12 = 12;
        setMeridiem(md);
        setHour12(String(h12).padStart(2, '0'));
        setMinute(String(m).padStart(2, '0'));

        let planForDefaults = plan;
        // [추가]
        // [변경 사유]
        // - 표시 영역(plan?.suggestion)도 함께 보강하기 위해, 조회로 찾은 플랜을 displayPlan에도 반영합니다.
        // - plan 객체가 전달된 경우(예: 건강 목표와 실천 계획 화면)에는 기존대로 plan을 사용합니다.
        // plan 객체가 없고 planId만 있는 경우(예: 기록 관리 화면에서 플랜 ID만 선택)에는
        // reportId 기준으로 플랜 목록을 조회하여 기본값 추천에 활용합니다.
        if ((!planForDefaults || !planForDefaults.plan_id) && planIdForCreate && reportId) {
          try {
            const { data } = await axios.get(`${apiUrl}/api/v1/action-plans/report/${reportId}`);
            const list = Array.isArray(data) ? data : [];
            const found = list.find((p) => String(p?.plan_id) === String(planIdForCreate));
            if (found) {
              planForDefaults = found;
              if (mounted) setDisplayPlan(found);
            }
          } catch (e) {
            // 조회 실패 시 기본값으로 폴백 (UX만 영향)
          }
        }

        // [추가]
        // [변경 사유]
        // - 생성 모드에서 planIdForCreate가 없고, plan 객체도 없는 경우에는 표시용 텍스트를 채울 방법이 없습니다.
        // - 다만 URL 진입/상태 전달 방식에 따라 createPlanId가 비어 있을 수 있으므로,
        //   edit 모드에서도 plan이 비어 있으면 동일한 경로로 plan 정보를 보강할 수 있도록 준비합니다.
        //   (현재는 생성 모드에서 주로 발생하는 이슈이므로, 호출 조건은 최소화합니다.)
        if (planForDefaults?.plan_id) {
          if (mounted) setDisplayPlan(planForDefaults);
        }

        const defaults = _deriveDefaultsFromPlan(planForDefaults);
        setForm({
          log_date: isoDate,
          performed_time: '',
          completion_status: 'completed',
          source: 'manual',
          quantity: defaults?.quantity ?? '',
          unit: defaults?.unit || 'min',
          user_rating: '',
          user_memo: '',
        });
      }

      if (!mounted) return;
      setError('');
      // 기본값 반영 완료
      setReady(true);

      // 단위(unit) 드롭다운 목록을 API로부터 비동기적으로 가져옵니다.
      try {
        const { data } = await axios.get(`${apiUrl}/api/v1/meta/units`);
        let list = Array.isArray(data) ? [...data] : [];
        // 'min' 옵션 보장
        if (!list.map(String).map(s => s.toLowerCase()).includes('min')) {
          list.push('min');
        }
        if (mounted) setUnits(list);
        if (mounted) setUnitsError('');
      } catch (e) {
        if (mounted) setUnits([]); // 폴백: 자유 입력
        if (mounted) setUnitsError(e?.message || '단위 목록을 불러오지 못했습니다. 입력창으로 대체합니다.');
      }
    })();
    return () => { mounted = false; };
  }, [open, logForEdit, planIdForCreate, reportId, plan, apiUrl]);

  if (!open) return null;
  // 초기값 준비 전에는 렌더링 지연(기본값이 입력창에 비어 보이는 문제 방지)
  if (!ready) return null;

  /**
   * [Why]
   * 사용자가 단위(unit)를 다양한 형태(예: '분(min)')로 선택하더라도, 백엔드 DB에는
   * 표준화된 영문 코드(예: 'min')로 일관되게 저장하기 위한 데이터 정규화 함수입니다.
   *
   * @param {string} u - 정규화할 단위 문자열.
   * @returns {string | undefined} 표준화된 단위 코드 또는 원본 값.
   */
  const normalizeUnit = (u) => {
    if (!u) return undefined;
    const s = String(u).trim().toLowerCase();
    // 괄호 내 코드 추출 우선
    const m = s.match(/\(([^)]+)\)/);
    const inParen = m ? m[1].trim().toLowerCase() : '';
    const candidate = inParen || s;

    // 명시적 매핑 (한국어/영문 코드 동시 매핑)
    if (['min','mins','minute','minutes','분'].includes(candidate)) return 'min';
    if (['hour','hours','hr','hrs','시간'].includes(candidate)) return 'hours';
    if (['km','킬로미터'].includes(candidate)) return 'km';
    if (['kcal','킬로칼로리'].includes(candidate)) return 'kcal';
    if (['count','회'].includes(candidate)) return 'count';
    if (['kg','킬로그램'].includes(candidate)) return 'kg';
    if (['percent','비율','%'].includes(candidate)) return 'percent';
    if (['bpm','심박수'].includes(candidate)) return 'bpm';
    if (['mg_dl','mg/dl','혈당'].includes(candidate)) return 'mg_dl';
    if (['mm_hg','mmhg','혈압'].includes(candidate)) return 'mm_hg';

    // 느슨한 포함 매핑(라벨 전체 문자열 등)
    if (s.includes('min')) return 'min';
    if (s.includes('hour') || s.includes('hr') || s.includes('시간')) return 'hours';
    if (s.includes('km') || s.includes('킬로미터')) return 'km';
    if (s.includes('kcal') || s.includes('킬로칼로리')) return 'kcal';
    if (s.includes('count') || s.includes('회')) return 'count';
    if (s.includes('kg') || s.includes('킬로그램')) return 'kg';
    if (s.includes('percent') || s.includes('비율') || s.includes('%')) return 'percent';
    if (s.includes('bpm') || s.includes('심박수')) return 'bpm';
    if (s.includes('mg/dl') || s.includes('mg_dl') || s.includes('혈당')) return 'mg_dl';
    if (s.includes('mmhg') || s.includes('mm_hg') || s.includes('혈압')) return 'mm_hg';

    return u; // 알 수 없으면 원본 유지
  };

  /**
   * [Why]
   * 사용자가 '저장' 버튼을 클릭했을 때, 입력된 폼 데이터를 취합하여 백엔드 API로
   * 전송하는 핵심 로직을 수행합니다.
   *
   * [How]
   * - `logForEdit` 존재 여부로 수정(PATCH) 또는 생성(POST) API를 분기합니다.
   * - `useRef`로 관리되는 각 입력 필드의 최신 값을 읽어옵니다.
   * - 시간 관련 입력(오전/오후, 시, 분)을 `YYYY-MM-DDTHH:mm` 형식의 ISO 문자열로 조합합니다.
   * - `axios`를 사용하여 API를 호출하고, 성공 시 `onSubmitted` 콜백을 실행하여 부모에게 알립니다.
   */
  const submit = async () => {
    setError('');
    try {
      const isEdit = !!logForEdit;
      const resolvedPlanId = isEdit ? logForEdit.plan_id : (plan?.plan_id || planIdForCreate);
      if (!resolvedPlanId) throw new Error('플랜 정보가 없습니다.');

      // 입력값 refs에서 읽기
      const dateVal = dateRef.current?.value || form.log_date || '';
      const hourVal = hourRef.current?.value || '';
      const minuteVal = minuteRef.current?.value || '';
      const qtyVal = qtyRef.current?.value ?? '';
      const unitValRaw = unitRef.current?.value ?? '';
      const ratingVal = ratingRef.current?.value ?? '';
      const memoVal = memoRef.current?.value ?? '';
      const statusVal = statusRef.current?.value || form.completion_status || 'completed';

      // AM/PM + 시/분을 24시간제 HH:mm로 변환
      let hh = parseInt(hourVal || '0', 10);
      let mm = parseInt(minuteVal || '0', 10);
      if (isNaN(hh) || hh < 1 || hh > 12) hh = 12;
      if (isNaN(mm) || mm < 0 || mm > 59) mm = 0;
      let h24 = hh % 12;
      if (meridiem === 'PM') h24 += 12;
      const pad = (n) => String(n).padStart(2, '0');
      const time24 = `${pad(h24)}:${pad(mm)}`;
      const performedLocal = dateVal ? `${dateVal}T${time24}` : undefined;

      const unitCode = normalizeUnit(unitValRaw || '');
      // log_date는 날짜만 전송하여 타임존 변환을 방지
      const dateOnly = dateVal || undefined;

      if (isEdit) {
        const bodyPatch = {
          completion_status: statusVal || undefined,
          performed_at: performedLocal ? new Date(performedLocal).toISOString() : undefined,
          log_date: dateOnly || undefined,
          quantity: qtyVal !== '' ? Number(qtyVal) : undefined,
          unit: unitCode || undefined,
          user_rating: ratingVal ? Number(ratingVal) : undefined,
          user_memo: memoVal || undefined,
        };
        await axios.patch(`${apiUrl}/api/v1/action-plans/${resolvedPlanId}/logs/${logForEdit.log_id}`, bodyPatch);
      } else {
        const bodyCreate = {
          log_date: dateOnly || undefined,
          performed_at: performedLocal ? new Date(performedLocal).toISOString() : new Date().toISOString(),
          completion_status: statusVal,
          source: form.source,
          quantity: qtyVal ? Number(qtyVal) : undefined,
          unit: unitCode || undefined,
          user_rating: ratingVal ? Number(ratingVal) : undefined,
          user_memo: memoVal || undefined,
          report_id: reportId || undefined,
        };
        await axios.post(`${apiUrl}/api/v1/action-plans/${resolvedPlanId}/logs`, bodyCreate);
      }
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || '저장 중 오류가 발생했습니다.';
      setError(msg);
    }
  };

  const Field = ({ label, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 13, color: '#374151' }}>{label}</label>
      {children}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
      <div style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ padding: 14, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 600 }}>{logForEdit ? '나의 건강 일기 : 실천 기록 수정' : '나의 건강 일기 : 실천 기록 추가'}</div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: 14, display: 'grid', gap: 12 }}>
          <div style={{ fontSize: 13, color: '#6b7280' }}>{plan?.suggestion || displayPlan?.suggestion || '실천 항목'}</div>

          <Field label="날짜">
            <input type="date"
              defaultValue={form.log_date}
              ref={dateRef}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 16 }} />
          </Field>

          <Field label="수행 시각">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ display: 'inline-flex', background: '#eef2ff', borderRadius: 8, overflow: 'hidden' }}>
                <button type="button"
                  onClick={() => setMeridiem('AM')}
                  style={{ padding: '6px 10px', border: 'none', background: meridiem === 'AM' ? '#6366f1' : 'transparent', color: meridiem === 'AM' ? '#fff' : '#111827', cursor: 'pointer' }}>
                  오전
                </button>
                <button type="button"
                  onClick={() => setMeridiem('PM')}
                  style={{ padding: '6px 10px', border: 'none', background: meridiem === 'PM' ? '#6366f1' : 'transparent', color: meridiem === 'PM' ? '#fff' : '#111827', cursor: 'pointer' }}>
                  오후
                </button>
              </div>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={12}
                placeholder="시"
                defaultValue={hour12}
                ref={hourRef}
                style={{ width: 64, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 16 }}
              />
              :
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={59}
                placeholder="분"
                defaultValue={minute}
                ref={minuteRef}
                style={{ width: 64, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 16 }}
              />
            </div>
          </Field>

          <Field label="상태">
            <select
              defaultValue={form.completion_status}
              ref={statusRef}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 16 }}>
              <option value="completed">완료</option>
              <option value="skipped">건너뜀</option>
              <option value="partially_completed">부분완료</option>
            </select>
          </Field>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="수량">
              <input type="number" inputMode="decimal" placeholder="예: 30"
                defaultValue={form.quantity}
                ref={qtyRef}
                style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 16 }} />
            </Field>
            <Field label="단위">
              {/* select(uncontrolled):
                  - 편집 모드: 기존 값이 목록에 있으면 그 값, 없으면 '선택'
                  - 생성 모드: 목록에 'min'이 있으면 'min', 없으면 '선택' */}
              <select
                key={`units-${Array.isArray(units) ? units.length : 0}`}
                defaultValue={(() => {
                  const list = Array.isArray(units) ? units : [];
                  const lower = list.map(String).map(s => s.toLowerCase());
                  const hasMin = lower.includes('min');
                  const cur = form.unit;
                  if (logForEdit) {
                    if (cur && lower.includes(String(cur).toLowerCase())) return cur;
                    return '';
                  }
                  // 생성 모드: form.unit(플랜 기반 추천)을 우선 적용하고, 없으면 min으로 폴백
                  if (cur && lower.includes(String(cur).toLowerCase())) return cur;
                  return hasMin ? 'min' : '';
                })()}
                ref={unitRef}
                style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 16 }}
              >
                {/* 기본값이 ''이면 placeholder 노출 */}
                {(() => {
                  const list = Array.isArray(units) ? units : [];
                  const lower = list.map(String).map(s => s.toLowerCase());
                  const hasMin = lower.includes('min');
                  const cur = form.unit;
                  const isEdit = !!logForEdit;
                  const defaultIsEmpty = isEdit ? !(cur && lower.includes(String(cur).toLowerCase())) : !hasMin;
                  return defaultIsEmpty ? <option value="">선택</option> : null;
                })()}

                {Array.isArray(units) && units.length > 0 && units.map(u => (
                  <option key={u} value={u}>{unitLabel(u)}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="만족도(1~5)">
            <select
              defaultValue={form.user_rating || ''}
              ref={ratingRef}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 16 }}>
              <option value="">선택</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </Field>

          <Field label="메모">
            <textarea rows={3}
              defaultValue={form.user_memo}
              ref={memoRef}
              style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, resize: 'vertical', fontSize: 16 }} />
          </Field>

          {error && <div style={{ color: '#dc2626', fontSize: 13 }}>{error}</div>}
          {unitsError && <div style={{ color: '#9ca3af', fontSize: 12 }}>{unitsError}</div>}
        </div>
        <div style={{ padding: 14, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={{ height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer' }}>취소</button>
          <button onClick={submit} style={{ height: 36, padding: '0 12px', borderRadius: 8, border: '1px solid #4f46e5', background: 'linear-gradient(180deg,#c7d2fe 0%,#a5b4fc 100%)', color: '#1e1b4b', cursor: 'pointer' }}>저장</button>
        </div>
      </div>
    </div>
  );
}
